import { EMessengerEventBusEvents } from '@xometry/ui';
import { ReactComponent as IconCheck } from 'assets/icons/solid-check.min.svg';
import cx from 'clsx';
import { LocaleEnum } from 'common/config/locales';
import { CURRENT_REGION } from 'common/config/regions/config';
import { Regions } from 'common/config/regions/types';
import {
  getRegionDefaultLocale,
  getRegionHost,
  getRegionIcon,
  getRegionsList,
  getRegionSupportedLocales,
} from 'common/config/regions/utils';
import { useMessengerEventBus } from 'common/hooks/useMessengerEventBus';
import { useRouteQuery } from 'common/hooks/useRouteQuery/useRouteQuery';
import { AntMenu, AntMenuItem, AntSubMenu } from 'components/ant/AntMenu';
import { t } from 'i18next';
import qs from 'qs';
import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { langIcons } from './LanguageChanger.const';
import styles from './LanguageChanger.module.less';

interface LanguageChangerProps {
  hasContact?: boolean;
}

const DTI = 'LanguageChanger';

const CheckedIcon = <IconCheck data-testid={`${DTI}__Checked`} className={styles.checkedIcon} />;
const RegionIcon = getRegionIcon();

const RegionsList = getRegionsList();

export const LanguageChanger: FC<LanguageChangerProps> = ({ hasContact }) => {
  const { i18n } = useTranslation();

  const { sendEvent } = useMessengerEventBus();
  const history = useHistory();
  const query = useRouteQuery();
  const locale = i18n.language as LocaleEnum;
  const languagesList = getRegionSupportedLocales();

  const regionTitle = t(`regions.${CURRENT_REGION.toLowerCase()}.short`);

  const langMenuTitle = (
    <div data-testid={`${DTI}__Title`} className={styles.langMenuTitle}>
      <span>{regionTitle}</span>
      <span className={styles.currentLocale}>{` | ${locale.toUpperCase()}`}</span>
    </div>
  );

  const handleChangeLocale = useCallback(
    async (newLang: LocaleEnum | string, navigate: 'push' | 'replace' = 'push') => {
      // Switch before the URL changes: the URL sync below must see the new language, or it applies it again.
      await i18n.changeLanguage(newLang);
      localStorage.setItem('currentLanguage', newLang);
      const data = qs.parse(location.search.replace('?', ''));
      history[navigate]({
        search: `?${qs.stringify({ ...data, locale: newLang })}`,
      });
      sendEvent?.(EMessengerEventBusEvents.CHANGE_LANGUAGE, { newLocale: newLang });
    },
    [history, i18n, sendEvent],
  );

  const handleChangeRegion = useCallback((region: Regions) => {
    if (region === CURRENT_REGION) {
      return;
    }

    const host = getRegionHost(region);
    const path = location.pathname;

    window.location.href = `${host}${path}`;
  }, []);

  useEffect(() => {
    const queryLocale = query.get('locale');
    const requestedLocale = queryLocale || localStorage.getItem('currentLanguage');
    const newLocale = languagesList.includes(requestedLocale as LocaleEnum)
      ? (requestedLocale as LocaleEnum)
      : getRegionDefaultLocale();

    if (queryLocale === newLocale && i18n.language === newLocale) {
      return;
    }

    // Keeping the URL in sync is not a navigation of its own: replace, so Back still leaves the page.
    void handleChangeLocale(newLocale, 'replace');
  }, [handleChangeLocale, i18n, languagesList, query]);

  return (
    <AntMenu className={styles.container} theme="light" mode="horizontal" triggerSubMenuAction="click">
      <AntSubMenu
        key="SubMenu"
        icon={<RegionIcon className={styles.regionIcon} />}
        title={langMenuTitle}
        className={cx(styles.subMenu)}
        popupClassName="langPopupMenu"
      >
        {!hasContact ? (
          <>
            <AntMenuItem key="regionsHeader" className={styles.regionsListHeader}>
              {`${t('commonPhrases.region')}:`}
            </AntMenuItem>
            {RegionsList.map((region, i, list) => {
              const key = `region_${region}`;
              const Icon = getRegionIcon(region);
              const ItemIcon = CURRENT_REGION === region ? CheckedIcon : undefined;
              const onClick = () => handleChangeRegion(region);
              const text = t(`regions.${region.toLowerCase()}.full`);

              const classes = cx({
                [styles.menuItem]: true,
                [styles.lastRegionMenuItem]: i === list.length - 1,
              });

              return (
                <AntMenuItem
                  data-testid={`${DTI}__Region`}
                  data-testvalue={region}
                  className={classes}
                  key={key}
                  icon={<Icon />}
                  itemIcon={ItemIcon}
                  onClick={onClick}
                >
                  {text}
                </AntMenuItem>
              );
            })}
          </>
        ) : null}
        <>
          <AntMenuItem key="localesHeader" className={styles.localesListHeader}>
            {`${t('commonPhrases.language')}:`}
          </AntMenuItem>
          {languagesList.map((l) => (
            <AntMenuItem
              data-testid={`${DTI}__Locale`}
              data-testvalue={l}
              key={l}
              className={styles.menuItem}
              icon={langIcons[l]}
              itemIcon={l === locale ? CheckedIcon : undefined}
              onClick={() => void handleChangeLocale(l)}
            >
              {t(`languages.${l}`)}
            </AntMenuItem>
          ))}
        </>
      </AntSubMenu>
    </AntMenu>
  );
};
